import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Check, 
  Eye, 
  Clock, 
  X,
  ChevronRight,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/contexts/AuthContext';

/**
 * JCE Send Confirmation Modal - Phase 4
 * 
 * Shows at Send/Preview time for CRITICAL items only (score >= 80).
 * - Buttons: Include | Offer | Inspect | N/A (fixed positions)
 * - Default = Offer if FOH does nothing
 * - Interaction time budget: <= 5 seconds average
 * - Never blocks sending - can be skipped entirely
 */

const JCESendConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  estimateId,
  jobGroups = [],
  vehicleContext = {}
}) => {
  const [criticalItems, setCriticalItems] = useState([]);
  const [decisions, setDecisions] = useState({});
  const [loading, setLoading] = useState(true);  // Start with loading=true to prevent auto-close before API call
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [startTime, setStartTime] = useState(null);

  // Extract job tags from job groups (JCE is single source of truth)
  const extractJobTags = () => {
    return jobGroups
      .filter(jg => jg.job_tag)
      .map(jg => jg.job_tag);
  };

  // Fetch critical items when modal opens
  useEffect(() => {
    if (isOpen && estimateId) {
      setStartTime(Date.now());
      fetchCriticalItems();
    }
  }, [isOpen, estimateId, jobGroups]);

  const fetchCriticalItems = async () => {
    setLoading(true);
    try {
      // Extract job tags from job_groups
      const jobTags = extractJobTags();
      
      if (jobTags.length === 0) {
        setCriticalItems([]);
        setLoading(false);
        return;
      }

      const response = await axios.post(`${API}/jce/evaluate`, {
        estimate_id: estimateId,
        job_tags: jobTags,
        vehicle_context: {
          reg: vehicleContext.reg || '',
          age_years: vehicleContext.age_years || 0,
          mileage: vehicleContext.mileage || 0,
          mileage_band: getMileageBand(vehicleContext.mileage || 0),
          fuel_type: (vehicleContext.fuel_type || 'petrol').toLowerCase(),
          make: vehicleContext.make,
          model: vehicleContext.model,
          year: vehicleContext.year
        },
        current_lines: jobGroups.map(jg => ({
          description: jg.job_name || jg.title || '',
          parts: jg.parts_lines || []
        }))
      });

      // Get only critical items (max 2 as per display limits)
      const critical = response.data.visible_critical || [];
      console.log('JCE Modal - API returned critical items:', critical.length, critical);
      setCriticalItems(critical.slice(0, 2));

      // Initialize decisions with defaults (Offer)
      const defaults = {};
      critical.slice(0, 2).forEach(item => {
        defaults[item.completeness_item_id] = item.default_decision || 'offer';
      });
      setDecisions(defaults);

    } catch (err) {
      console.error('Failed to fetch critical items:', err);
      setCriticalItems([]);
    } finally {
      setLoading(false);
    }
  };

  const getMileageBand = (mileage) => {
    if (mileage < 40000) return '<40k';
    if (mileage < 80000) return '40-80k';
    if (mileage < 120000) return '80-120k';
    return '120k+';
  };

  const handleDecision = (itemId, decision) => {
    setDecisions(prev => ({
      ...prev,
      [itemId]: decision
    }));
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    
    try {
      // Record all decisions
      const decisionsToRecord = Object.entries(decisions).map(([itemId, decision]) => ({
        estimate_id: estimateId,
        completeness_item_id: itemId,
        decision_type: decision
      }));

      if (decisionsToRecord.length > 0) {
        await axios.post(`${API}/jce/decisions/bulk`, {
          estimate_id: estimateId,
          decisions: decisionsToRecord
        });
      }

      // Log interaction time for analytics
      if (startTime) {
        const interactionTime = (Date.now() - startTime) / 1000;
        console.log(`JCE Send confirmation interaction time: ${interactionTime.toFixed(2)}s`);
      }

      setSubmitted(true);
      
      // Brief delay to show "Scope recorded" before proceeding
      setTimeout(() => {
        onConfirm && onConfirm(decisions);
      }, 800);

    } catch (err) {
      console.error('Failed to record decisions:', err);
      // Don't block - proceed anyway
      onConfirm && onConfirm(decisions);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    // Use defaults for all items
    const defaultDecisions = {};
    criticalItems.forEach(item => {
      defaultDecisions[item.completeness_item_id] = 'offer';
    });
    
    // Record defaults silently
    if (criticalItems.length > 0) {
      axios.post(`${API}/jce/decisions/bulk`, {
        estimate_id: estimateId,
        decisions: Object.entries(defaultDecisions).map(([itemId, decision]) => ({
          estimate_id: estimateId,
          completeness_item_id: itemId,
          decision_type: decision
        }))
      }).catch(err => console.error('Failed to record default decisions:', err));
    }

    onConfirm && onConfirm(defaultDecisions);
  };

  if (!isOpen) return null;

  // If no critical items, auto-proceed
  if (!loading && criticalItems.length === 0) {
    // Auto close and proceed after brief moment
    setTimeout(() => {
      onConfirm && onConfirm({});
    }, 100);
    return null;
  }

  // Submitted state - show "Scope recorded"
  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="jce-send-modal">
        <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 text-center">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-6 h-6 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800">Scope recorded</h3>
          <p className="text-sm text-slate-500 mt-1">Proceeding to preview...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="jce-send-modal">
      <div className="bg-white rounded-xl max-w-lg w-full mx-4 overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-slate-700 text-white p-4">
          <h3 className="font-semibold">Repair Scope Check</h3>
          <p className="text-slate-300 text-sm">
            {criticalItems.length} commonly completed item{criticalItems.length !== 1 ? 's' : ''} to review
          </p>
        </div>

        {/* Content */}
        <div className="p-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-600 rounded-full mx-auto mb-3"></div>
              <p className="text-sm text-slate-500">Loading suggestions...</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-600 mb-4">
                How should these appear to the customer?
              </p>

              {criticalItems.map((item, index) => (
                <CriticalItemDecision
                  key={item.completeness_item_id}
                  item={item}
                  decision={decisions[item.completeness_item_id]}
                  onDecision={(d) => handleDecision(item.completeness_item_id, d)}
                  index={index}
                />
              ))}

              <p className="text-xs text-slate-400 mt-4 text-center">
                Default: "Offer as repair choice" if no selection
              </p>
            </div>
          )}
        </div>

        {/* Footer - Fixed button positions */}
        <div className="border-t border-slate-200 p-4 bg-slate-50 flex justify-between items-center">
          <button
            onClick={handleSkip}
            className="text-sm text-slate-500 hover:text-slate-700"
            data-testid="jce-send-skip"
          >
            Skip (use defaults)
          </button>
          <Button
            onClick={handleConfirm}
            disabled={submitting || loading}
            className="bg-slate-700 hover:bg-slate-800"
            data-testid="jce-send-confirm"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Recording...
              </>
            ) : (
              <>
                Continue to Preview
                <ChevronRight className="w-4 h-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Individual Critical Item Decision Row
 * Buttons in fixed positions: Include | Offer | Inspect | N/A
 */
const CriticalItemDecision = ({ item, decision, onDecision, index }) => {
  const buttons = [
    { id: 'include', label: 'Include', icon: Check, color: 'green', title: 'Include in estimate' },
    { id: 'offer', label: 'Offer', icon: Eye, color: 'amber', title: 'Offer to customer' },
    { id: 'inspect', label: 'Inspect', icon: Clock, color: 'blue', title: 'Confirm after strip' },
    { id: 'not_applicable', label: 'N/A', icon: X, color: 'slate', title: 'Not applicable' }
  ];

  const getButtonClass = (btnId, btnColor) => {
    const isActive = decision === btnId;
    const baseClass = 'flex-1 py-2 px-2 text-xs font-medium rounded transition-all flex flex-col items-center gap-1';
    
    if (isActive) {
      switch (btnColor) {
        case 'green': return `${baseClass} bg-green-500 text-white`;
        case 'amber': return `${baseClass} bg-amber-500 text-white`;
        case 'blue': return `${baseClass} bg-blue-500 text-white`;
        default: return `${baseClass} bg-slate-500 text-white`;
      }
    }
    return `${baseClass} bg-slate-100 text-slate-600 hover:bg-slate-200`;
  };

  return (
    <div 
      className="bg-slate-50 rounded-lg p-3 border border-slate-200"
      data-testid={`jce-critical-item-${index}`}
    >
      <div className="mb-3">
        <p className="font-medium text-slate-800 text-sm">{item.title}</p>
        {item.internal_note && (
          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{item.internal_note}</p>
        )}
      </div>
      
      {/* Fixed position buttons */}
      <div className="flex gap-2" data-testid={`jce-decision-buttons-${index}`}>
        {buttons.map(btn => (
          <button
            key={btn.id}
            onClick={() => onDecision(btn.id)}
            className={getButtonClass(btn.id, btn.color)}
            title={btn.title}
            data-testid={`jce-btn-${btn.id}-${index}`}
          >
            <btn.icon className="w-4 h-4" />
            <span>{btn.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default JCESendConfirmationModal;
